"use client";

import { useRef, useState } from "react";
import TrackedLink from "./TrackedLink";
import { track } from "@lib/track";

/**
 * Name + email gate in front of a CF-owned retreat brochure.
 *
 * Nine months of event logs show 257 distinct sessions clicking to download a
 * brochure and exactly one leaving contact details, so every one of those
 * prospects had to be converted by Niki personally. This is the capture step.
 *
 * Deliberately a **soft** gate: the PDFs live in `/public`, so anyone holding the
 * direct URL still gets the file. Gating the CTA is the realistic ceiling
 * without moving the brochures behind a route, and it is aimed at the ~18
 * sessions a month who arrive via the page rather than at the handful who
 * deep-link.
 *
 * Only used where `retreat.type === 'cf'`. Partner retreats (Yoga on the Edge,
 * Flamingo Falls) book on someone else's site, so a captured lead is worth much
 * less and their brochure stays a plain link.
 *
 * Tracking, which matters here because a report already depends on it:
 *  - The **existing** `click` event fires on the button with its existing label,
 *    unchanged. That keeps the nine-month brochure-click series continuous and
 *    makes clicked-versus-captured measurable.
 *  - `retreat_interest` fires **only** after the API confirms the capture —
 *    never on submit, never on the error path. Label is the retreat id, matching
 *    the `{id} - Brochure` click labels so a report can group the two together.
 */

type Props = {
  retreatId: string;
  retreatName: string;
  brochureLink: string;
  /** Label for the existing click event — passed through verbatim. */
  label: string;
  component: string;
  variant?: "solid" | "outline" | "ghost" | "text";
  size?: "sm" | "md" | "lg";
  color?: "base" | "duck" | "oat" | "neutral";
  children?: React.ReactNode;
};

export default function BrochureGate({
  retreatId,
  retreatName,
  brochureLink,
  label,
  component,
  variant = "outline",
  size = "md",
  color = "neutral",
  children = "Download Brochure",
}: Props) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [hp, setHp] = useState(""); // honeypot — the server makes the real decision
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const openedAt = useRef<number>(0);

  const openGate = () => {
    openedAt.current = Date.now();
    setOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // one capture per submit, so one retreat_interest event
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch("/api/retreat-interest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          retreatId,
          hp,
          elapsedMs: Date.now() - openedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) {
        setError(data?.message || "Something went wrong. Please try again.");
        return; // retreat_interest must NOT fire on the error path
      }

      // Success only, and after the API has resolved.
      try {
        track(
          "retreat_interest",
          { label: retreatId, component },
          { event: "retreat_interest", label: retreatId, component }
        );
      } catch {}

      setDone(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-muted)] p-4 max-w-sm">
        <p className="text-sm text-[var(--color-text-primary)] mb-3">
          Sent — check your inbox for the {retreatName} brochure.
        </p>
        {/*
          Rendered as a real link for the visitor to click rather than opened
          automatically: a window.open() after an awaited fetch is outside the
          user gesture and gets swallowed by popup blockers.
        */}
        <TrackedLink
          href={brochureLink}
          newTab
          variant="solid"
          size="md"
          color="base"
          event="click"
          label={`${retreatId} - Brochure Opened`}
          component={component}
        >
          Open it now
        </TrackedLink>
      </div>
    );
  }

  if (!open) {
    return (
      <TrackedLink
        variant={variant}
        size={size}
        color={color}
        event="click"
        label={label}
        component={component}
        onClick={openGate}
      >
        {children}
      </TrackedLink>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-muted)] p-4 max-w-sm text-left"
    >
      <p className="text-sm font-semibold text-[var(--color-text-brand)] mb-1">
        Where shall we send it?
      </p>
      <p className="text-xs text-[var(--color-text-secondary)] mb-3">
        We&rsquo;ll email you the {retreatName} brochure and let you know when new
        dates open.
      </p>

      <label className="sr-only" htmlFor={`bg-name-${retreatId}`}>Name</label>
      <input
        id={`bg-name-${retreatId}`}
        type="text"
        required
        autoComplete="name"
        placeholder="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full mb-2 rounded-lg border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-text-ink)]"
      />

      <label className="sr-only" htmlFor={`bg-email-${retreatId}`}>Email</label>
      <input
        id={`bg-email-${retreatId}`}
        type="email"
        required
        autoComplete="email"
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="w-full mb-3 rounded-lg border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-[var(--color-text-ink)]"
      />

      {/* Honeypot — hidden from people, irresistible to form-fillers. */}
      <input
        type="text"
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        className="absolute left-[-9999px] w-px h-px opacity-0"
      />

      {error && (
        <p className="text-xs text-red-600 mb-2" role="alert">
          {error}
        </p>
      )}

      <TrackedLink
        type="submit"
        variant="solid"
        size="md"
        color="base"
        disabled={submitting}
        event="click"
        label={`${retreatId} - Brochure Submit`}
        component={component}
      >
        {submitting ? "Sending…" : "Send me the brochure"}
      </TrackedLink>
    </form>
  );
}
